import { isTravelCardRate } from './travelCardAnalytics'

const getSlashedPricing = itinerary =>
  (itinerary?.itineraryInfo?.reservationItems || [])
    .map(item => item?.ferry?.slashedPricing)
    .find(Boolean) || null

const getSlashedTrigger = (itinerary, slashedPricing) => {
  if (slashedPricing?.shown !== true) return 'none'
  if (itinerary?.hotels?.length || itinerary?.accommodations?.length) {
    return 'accommodation_bundle'
  }
  const { trigger } = slashedPricing || {}
  if (trigger && trigger !== 'none') return trigger
  return 'non_default_product_code'
}

const getLegDiscount = (slashedPricing, crossing, index) => {
  if (!slashedPricing?.shown) return 0
  const legs = slashedPricing?.legs || []
  const leg =
    (crossing?.routeCode &&
      legs.find(item => item?.routeCode === crossing.routeCode)) ||
    legs[index]
  if (!leg) return 0
  const discount =
    leg?.discount != null
      ? Number(leg.discount)
      : Number(leg?.standardPrice) - Number(leg?.chargedPrice)
  return Number.isFinite(discount) && discount > 0 ? discount : 0
}

export const formatItineraryForAnalytics = (itinerary, paymentSource) => {
  if (!itinerary) {
    return {}
  }

  const items = []
  const slashedPricing = getSlashedPricing(itinerary)
  // `shown` is the back end's own record of whether the customer saw a
  // strikethrough - it is already false when the standard price call failed,
  // returned nothing, or came back no higher than the price charged.
  const wasSlashed = slashedPricing?.shown === true

  if (itinerary?.ferries?.length) {
    itinerary.ferries.forEach(ferry => {
      if (ferry?.crossings?.length) {
        ferry?.crossings?.forEach((crossing, index) => {
          items.push({
            item_name: `ferry crossing ${crossing?.routeCode || ''}`,
            item_id: ferry.rph,
            // stays the amount actually charged - swapping in the standard price
            // here would inflate reported revenue
            price: crossing?.priceData?.total,
            discount: getLegDiscount(slashedPricing, crossing, index),
            item_brand: 'Red Funnel',
            item_category: 'Ferry',
            quantity: 1,
          })
        })
      }
    })
  }
  if (itinerary?.hotels?.length) {
    itinerary.hotels.forEach(hotel => {
      items.push({
        item_name: hotel.hotelName || hotel.hotelCode || '',
        item_id: hotel.bookingReference || '',
        price: hotel.price || '',
        // slashed pricing applies to the crossings only, but the field is set on
        // every item so its absence never has to be interpreted
        discount: 0,
        item_brand: hotel.hotelCode || 'Red Funnel',
        item_category: 'Accommodations',
        quantity: 1,
      })
    })
  }

  if (itinerary?.attractions?.length) {
    itinerary.attractions.forEach(attraction => {
      items.push({
        item_name: attraction.description || '',
        item_id: attraction.resourceCode,
        price: attraction.price || '',
        discount: 0,
        item_brand: attraction.supplierCode || '',
        item_category: 'Attractions',
        quantity: attraction.amount || 1,
      })
    })
  }

  return {
    payment_type: paymentSource,
    // read from the booking record: the ferry was priced at a Travel Card
    // special-rate product code
    travel_card_rate: isTravelCardRate(itinerary?.ferryProductCode)
      ? 'true'
      : 'false',
    // Always sent, on every booking. Left out, "no discount" could not be told
    // apart from "not tracked", and that cannot be corrected after the fact.
    slashed_pricing: wasSlashed ? 'true' : 'false',
    slashed_pricing_trigger: getSlashedTrigger(itinerary, slashedPricing),
    // the feed already reports 0 when nothing was shown, so it is taken as given
    slashed_pricing_saving: Number(slashedPricing?.saving) || 0,
    ecommerce: {
      currency: 'GBP',
      value: itinerary?.grandTotal ? +itinerary.grandTotal : '',
      affiliation: 'Red Funnel',
      transaction_id: itinerary?.bookingNumber,
      items,
    },
  }
}

const hashEmail = async email => {
  const encoder = new TextEncoder()
  const data = encoder.encode(email.trim().toLowerCase())
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray
    .map(val => val.toString(16).padStart(2, '0'))
    .join('')
  return hashHex
}

export const formatEpsilonConversionForAnalytics = async (itinerary, user) => {
  if (!itinerary) {
    return {}
  }

  const items = []

  if (itinerary?.ferries?.length) {
    itinerary.ferries.forEach(ferry => {
      if (ferry?.crossings?.length) {
        ferry?.crossings?.forEach(crossing => {
          items.push({
            product_id: crossing?.routeCode,
            item_amount: crossing?.priceData?.total,
            item_quantity: '1',
            item_discount: crossing?.priceData?.discount,
          })
        })
      }
      if (
        ferry?.cancellationProtection &&
        ferry?.cancellationProtection?.included
      ) {
        items.push({
          product_id:
            ferry?.cancellationProtection?.resourceCode ||
            ferry?.cancellationProtection?.supplierCode ||
            '',
          item_amount: `${ferry?.cancellationProtection?.price}`,
          item_quantity: '1',
          item_discount: '0.00',
        })
      }
    })
  }
  if (itinerary?.donations?.length) {
    itinerary.donations.forEach(donation => {
      items.push({
        product_id: donation?.resourceCode || donation?.supplierCode || '',
        item_amount: `${donation.price}` || '',
        item_quantity: '1',
        item_discount: '0.00',
      })
    })
  }
  if (itinerary?.hotels?.length) {
    itinerary.hotels.forEach(hotel => {
      items.push({
        product_id: hotel.hotelCode || hotel.resourceCode || '',
        item_amount: `${hotel.price}` || '',
        item_quantity: '1',
        item_discount: hotel.discount || '',
      })
    })
  }

  if (itinerary?.attractions?.length) {
    itinerary.attractions.forEach(attraction => {
      items.push({
        product_id: attraction.resourceCode || '',
        item_amount: `${attraction.price}` || '',
        item_quantity: `${attraction.amount}` || '1',
        item_discount: attraction.discount || '',
      })
    })
  }

  const base = {
    dtmc_transaction_id: itinerary.bookingNumber,
    dtm_conv_val: itinerary.totalCost,
    dtm_conv_curr: 'GBP',
    dtmc_site_language: 'EN',
    dtm_items: items,
  }

  if (user) {
    const hashedEmail = await hashEmail(user.email)
    return {
      ...base,
      dtm_user_id: user.customerNumber,
      dtm_email_hash: hashedEmail,
      dtmc_client_defined_country: user.countryName ?? '',
    }
  }

  return base
}

export const formatEpsilonActionConversionForAnalytics = async user => {
  if (!user) {
    return {}
  }
  const hashedEmail = await hashEmail(user.email)
  return {
    dtm_user_id: user.customerNumber,
    dtm_email_hash: hashedEmail,
    dtmc_client_defined_country: user.countryName ?? '',
    dtmc_site_language: 'EN',
  }
}

const getPromoId = (pathname, length) => {
  switch (true) {
    case pathname.includes('/my-account'):
      return '942'
    case pathname.includes('/payment'):
      return '8'
    case pathname.includes('/review'):
      return '6'
    case pathname.includes('/customer-details'):
      return '6'
    case pathname.includes('/summary'):
      return '6'
    case length === 1:
      return '2'
    case length === 2:
      return '3'
    case length >= 3:
      return '5'
    default:
      return '1'
  }
}

export const formatEpsilonPageVisitForAnalytics = async (user, pathname) => {
  if (
    pathname.includes('agent-portal') ||
    pathname.includes('contact-centre') ||
    pathname.includes('book/summary')
  )
    return {}

  const segments = pathname.split('/').filter(Boolean)
  const [dept = 'home', category = '', ...rest] = segments
  const product = rest.join(', ')
  const promoId = getPromoId(pathname, segments?.length)

  const base = {
    dtmc_department: dept,
    dtmc_category: category,
    dtmc_product_id: product,
    promo_id: promoId,
    dtmc_site_language: 'EN',
  }

  if (user) {
    const hashedEmail = await hashEmail(user.email)
    return {
      ...base,
      dtm_user_id: user.customerNumber,
      dtm_email_hash: hashedEmail,
      dtmc_client_defined_country: user.countryName ?? '',
    }
  }

  return base
}
